import React from 'react';
import InfoIcon from '@material-ui/icons/Info';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';
import { useTranslation, Trans } from 'react-i18next';
import StatItem from 'components/@common/StatItem';
import { entries, flow, groupBy, map, partition } from 'lodash/fp';
import { Button, Divider, Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import useProductCache, { PliManagedApp } from 'store/cache/product-cache';
import { aggregateUsages } from 'utilities/product-utils';
import { OrganizationLicenseUsage, Product, ProductType } from 'types/schema-generated-types';
import LicenseChart from './components/LicenseChart';
import ProductsTable from './components/ProductsTable';

interface ProductFamilyUsageCardProps {
  licenseUsage: OrganizationLicenseUsage[];
  rootOrganizationLicenseUsage?: OrganizationLicenseUsage[];
}

interface ProductUsageProps {
  licenseUsage: OrganizationLicenseUsage[];
  rootOrganizationLicenseUsage?: OrganizationLicenseUsage[];
  product: Product;
}

interface OverageWarningProps {
  app?: PliManagedApp | null;
  count?: number;
}

type ProductLicenseUsage = [
  Product,
  OrganizationLicenseUsage[],
  OrganizationLicenseUsage[] | undefined
];

const OverageWarning = ({ app, count = 0 }: OverageWarningProps) => {
  const classes = useStyles();
  const { t } = useTranslation('ProductAndLicenses');

  return (
    <Grid item container className={classes.overageWarningRoot} justify="space-between" xs>
      <Grid item>
        <Typography>
          {count > 0 ? (
            /* prettier-ignore */
            <Trans t={t} i18nKey="usingLicenseOverage" count={count}>
            Your license usage is above your purchased licenses by <strong>{{ count }} license</strong>
          </Trans>
          ) : (
            t('noLicensesAvailable')
          )}
        </Typography>
        <Typography>{t('callToAction')}</Typography>
      </Grid>
      <Grid item container spacing={2} xs justify="flex-end">
        {app?.purchaseDetails?.webstoreUrl && (
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShoppingCartIcon />}
              href={app.purchaseDetails.webstoreUrl}
              target="_blank"
            >
              {t('orderOnline')}
            </Button>
          </Grid>
        )}
        {app?.purchaseDetails?.salesQuoteUrl && (
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={<InfoIcon />}
              href={app.purchaseDetails.salesQuoteUrl}
              target="_blank"
            >
              {t('requestQuote')}
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const ProductUsage = ({
  product,
  licenseUsage,
  rootOrganizationLicenseUsage
}: ProductUsageProps) => {
  const classes = useStyles();
  const [{ findAppByProductCode }] = useProductCache();

  let {
    siteLicensed,
    assignedCount,
    realAvailableCount,
    realTotalCount,
    expiringCount
  } = aggregateUsages(licenseUsage);

  // If this is a suborganization (i.e. rootOrganizationLicenseUsage exists here), we need to calculate things a little differently...
  if (rootOrganizationLicenseUsage) {
    // Overage and available licenses are calculated at the root level, get the root's 'real' available license count.
    const rootRealAvailableCount = aggregateUsages(rootOrganizationLicenseUsage).realAvailableCount;
    if (rootRealAvailableCount > 0) {
      realAvailableCount = rootRealAvailableCount; // If there are *any* available licenses at the root, then they'll be available to any suborganization.
      realTotalCount = assignedCount + realAvailableCount; // Total count here will be the suborganization's assigned count plus root's available count.
    }
  }

  const overageCount = assignedCount > realTotalCount ? assignedCount - realTotalCount : 0;
  const availableCount = overageCount > 0 ? 0 : realAvailableCount;

  const showOverageWarning = !siteLicensed && (availableCount === 0 || overageCount > 0);

  const app = findAppByProductCode(product.code);

  return (
    <Grid item container className={classes.productRoot}>
      <Grid item container xs={12}>
        <Grid container item spacing={2} alignItems="center">
          <Grid item>
            <img src={product.iconUrl ?? ''} alt={product.name} height={32} width={32} />
          </Grid>
          <Grid item>
            <Typography variant="h3">{product.name}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container className={classes.statContainer}>
        <Grid item xs={6} sm={1}>
          <StatItem size="small" title="Total" count={realTotalCount} />
        </Grid>
        <Grid item xs={6} sm={1}>
          <StatItem
            size="small"
            title="Assigned"
            count={assignedCount}
            {...(overageCount > 0 && { severity: 'error' })}
          />
        </Grid>
        <Grid item xs={6} sm={1}>
          <StatItem size="small" title="Available" count={availableCount} />
        </Grid>
        <Grid item xs={6} sm={1}>
          <StatItem size="small" title="Exp. Soon" count={expiringCount} />
        </Grid>
        {showOverageWarning && <OverageWarning app={app} count={overageCount} />}
      </Grid>
      <Grid container item className={classes.reportContainer}>
        <Grid item xs={4} className={classes.chartContainer}>
          <LicenseChart
            totalCount={realTotalCount}
            assignedCount={assignedCount}
            expiringSoonCount={expiringCount}
            overageCount={overageCount}
          />
        </Grid>
        <Grid item xs={8}>
          <ProductsTable licenseUsage={licenseUsage} />
        </Grid>
      </Grid>
    </Grid>
  );
};

const ProductFamilyUsageCard = ({
  licenseUsage,
  rootOrganizationLicenseUsage
}: ProductFamilyUsageCardProps) => {
  const classes = useStyles();

  const [studentProductLicenseUsage, userProductLicenseUsage]: [
    ProductLicenseUsage[],
    ProductLicenseUsage[]
  ] = flow(
    groupBy('product.id'),
    entries,
    map(([productId, usages]: [number, OrganizationLicenseUsage[]]) => {
      const rootUsages = rootOrganizationLicenseUsage?.filter(u => u.productId === +productId);
      return [usages[0].product, usages, rootUsages];
    }),
    partition(([product]: [Product]) => product.type === ProductType.STUDENT)
  )(licenseUsage);

  const showStudentUsage = studentProductLicenseUsage.length > 0;
  const showUserUsage = userProductLicenseUsage.length > 0;

  return (
    <Grid component={Paper} direction="column" container className={classes.root}>
      {showStudentUsage &&
        studentProductLicenseUsage.map(([product, licenseUsage, rootUsages]) => (
          <ProductUsage
            rootOrganizationLicenseUsage={rootUsages}
            licenseUsage={licenseUsage}
            product={product}
          />
        ))}
      {showStudentUsage && showUserUsage && <Divider className={classes.divider} />}
      {showUserUsage &&
        userProductLicenseUsage.map(([product, licenseUsage, rootUsages]) => (
          <ProductUsage
            rootOrganizationLicenseUsage={rootUsages}
            licenseUsage={licenseUsage}
            product={product}
          />
        ))}
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1)
  },
  productRoot: {
    padding: theme.spacing(1)
  },
  overageWarningRoot: {
    border: `2px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
    marginLeft: theme.spacing(2),
    padding: theme.spacing(2)
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  chartContainer: {
    alignSelf: 'center'
  },
  statContainer: {
    marginTop: theme.spacing(1)
  },
  reportContainer: {
    marginTop: theme.spacing(1)
  }
}));

export default ProductFamilyUsageCard;
